import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import KeyIcon from '@mui/icons-material/Key';
import LockIcon from '@mui/icons-material/Lock';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloseIcon from '@mui/icons-material/Close';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InstructionsDialog = ({ open, onClose }) => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="instructions-dialog-title"
    >
      <DialogTitle id="instructions-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">How to Use This Application</Typography>
        <Button 
          onClick={onClose} 
          color="inherit" 
          sx={{ minWidth: 'auto', p: 1 }}
          aria-label="close"
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="instruction tabs"
          variant="fullWidth"
        >
          <Tab label="Encryption" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Decryption" id="tab-1" aria-controls="tabpanel-1" />
          <Tab label="Security" id="tab-2" aria-controls="tabpanel-2" />
        </Tabs>
      </Box>

      <DialogContent>
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Encrypting and Uploading Files
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <UploadFileIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Upload your image" 
                secondary="Select a file using the browse button or drag and drop an image into the designated area. Supported formats include PNG, JPG, JPEG, and GIF."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <KeyIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Create a strong encryption key" 
                secondary="Enter a secure encryption key that you'll remember. This key will be needed to decrypt your file later. For best security, use at least 12 characters with a mix of letters, numbers, and special characters."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LockIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Encrypt your file" 
                secondary="Click the 'Encrypt File' button to encrypt your image. This process happens entirely in your browser - your unencrypted data is never sent to any server."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <DownloadIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Save the encrypted data" 
                secondary="After encryption, you can download the encrypted data as a text file. This file contains the encrypted version of your image that can only be decrypted with your key."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <CloudUploadIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Upload to Blob Storage (optional)"
                secondary="You can store your encrypted data on Vercel Blob Storage by clicking the 'Upload to Blob Storage' button. This will give you a secure link to your encrypted data that you can share."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LinkIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Save your storage link and encryption key"
                secondary="If you upload to blob storage, make sure to save both the storage link and your encryption key. You'll need both to decrypt your file later."
              />
            </ListItem>
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Decrypting Files
          </Typography>

          <List>
            <ListItem>
              <ListItemIcon>
                <KeyIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Enter your encryption key" 
                secondary="Input the same encryption key that was used when encrypting the file. Without the correct key, decryption will fail."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <ContentPasteIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Provide the encrypted data" 
                secondary="Either paste the encrypted data into the text area or load it from a file. If you saved the encrypted data as a text file, you can use the 'Load Encrypted Data File' button."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LockOpenIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Select the correct file format" 
                secondary="Choose the same file format that was selected during encryption. This ensures the file is correctly interpreted after decryption."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <LockOpenIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Decrypt the data" 
                secondary="Click the 'Decrypt Data' button to decrypt your file. If successful, you'll see a preview of your decrypted image."
              />
            </ListItem>

            <ListItem>
              <ListItemIcon>
                <DownloadIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Download the decrypted file" 
                secondary="Once decryption is complete, you can download the original file using the 'Download Decrypted File' button."
              />
            </ListItem>
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Security Information
          </Typography>

          <List>
            <ListItem>
              <ListItemText 
                primary="Client-Side Encryption" 
                secondary="All encryption and decryption happens in your browser. Your original files are never sent to our servers."
              />
            </ListItem>

            <ListItem>
              <ListItemText 
                primary="AES-256 Encryption" 
                secondary="We use industry-standard AES-256 encryption to protect your files. This is the same level of encryption used by governments and financial institutions."
              />
            </ListItem>

            <ListItem>
              <ListItemText 
                primary="Key Management" 
                secondary="Your encryption key is never stored or transmitted. Only you know your key, so keep it safe. If you lose your key, there is no way to recover your encrypted data."
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Blob Storage Security"
                secondary="When you upload to Vercel Blob Storage, only the encrypted data is stored. The file remains encrypted in storage and can only be accessed with the storage URL and decrypted with your key."
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Secure Transmission"
                secondary="All communication with Vercel Blob Storage uses HTTPS to ensure secure transmission of your encrypted data."
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Best Practices"
                secondary="For maximum security, use strong encryption keys, don't share your keys in the same channel as your storage links, and consider using a secure password manager to store your encryption keys."
              />
            </ListItem>
          </List>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructionsDialog;